/**
 * ThemeProvider — SSOT for theme state.
 *
 * Tri-state `'system' | 'light' | 'dark'`, persisted to localStorage and
 * reflected on `<html data-theme="...">`. `'system'` follows
 * `prefers-color-scheme` live. `meta[name=theme-color]` is kept in sync so
 * mobile browsers blend the chrome into the app's background.
 *
 * Exposes `useTheme()` for consumers (UserMenu, Profile Appearance, ⌘K
 * palette). Pure React context — no external store.
 */

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    useSyncExternalStore,
    type ReactNode,
} from 'react';
import { captureError } from '../errors/captureError';

export type ThemeChoice = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

type ThemeContextValue = {
    choice: ThemeChoice;
    resolved: ResolvedTheme;
    setChoice: (next: ThemeChoice) => void;
    cycle: () => void; // convenience for a simple toggle button
};

const STORAGE_KEY = 'chiefy.theme';
const COOKIE_KEY = 'chiefy_theme';
const ThemeContext = createContext<ThemeContextValue | null>(null);

function readCookieChoice(): ThemeChoice | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(?:^|; )chiefy_theme=([^;]+)/);
    if (!match) return null;
    const raw = decodeURIComponent(match[1] ?? '');
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
    return null;
}

function readStoredChoice(): ThemeChoice {
    // Cookie takes precedence over localStorage because it round-trips
    // across the BE auth ↔ SPA subdomain handoff (see CANONICAL_PALETTE.md
    // § "Theme switching"). Same-origin localStorage is the fallback for
    // first-visit users on the SPA before any cookie has been set.
    if (typeof window === 'undefined') return 'system';
    const fromCookie = readCookieChoice();
    if (fromCookie) return fromCookie;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
    return 'system';
}

function writeCookieChoice(value: ThemeChoice): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    try {
        const host = window.location.hostname;
        const isIp = /^\d+\.\d+\.\d+\.\d+$/.test(host);
        let domainAttr = '';
        if (host !== 'localhost' && !isIp) {
            const parts = host.split('.');
            if (parts.length >= 2) {
                domainAttr = '; Domain=.' + parts.slice(-2).join('.');
            }
        }
        const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${COOKIE_KEY}=${encodeURIComponent(value)}; Path=/; Expires=${expires}${domainAttr}${secure}; SameSite=Lax`;
    } catch {
        /* cookie unavailable — localStorage fallback covers same-origin */
    }
}

function prefersDark(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** useSyncExternalStore subscriber — fires when the OS theme flips.
 *  Drives the `resolved` theme when choice === 'system'. Returns a
 *  cleanup to unsubscribe. */
function subscribeOsTheme(cb: () => void): () => void {
    if (typeof window === 'undefined' || !window.matchMedia) return () => {};
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    mql.addEventListener?.('change', cb);
    return () => mql.removeEventListener?.('change', cb);
}

function getOsTheme(): ResolvedTheme { return prefersDark() ? 'dark' : 'light'; }
function getOsThemeSSR(): ResolvedTheme { return 'dark'; }

// `meta[name="theme-color"]` tints the browser chrome (iOS Safari
// status bar, Android Chrome address bar). Match `--bg-canvas` in both
// themes so the system chrome reads as a natural extension of the
// sidebar/topbar, not a patch of off-palette pixels.
const THEME_COLOR: Record<ResolvedTheme, string> = {
    dark: '#120825',   // --bg-canvas (dark)
    light: '#ece3d3',  // --bg-canvas (light)
};

function applyDomTheme(resolved: ResolvedTheme): void {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', resolved);
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'theme-color';
        document.head.appendChild(meta);
    }
    meta.content = THEME_COLOR[resolved];
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [choice, setChoiceState] = useState<ThemeChoice>(readStoredChoice);
    // Subscribe to the OS theme externally so we derive `resolved` with
    // pure useMemo instead of a setState-in-effect cascade. This is the
    // React 19 idiomatic shape — the value is always consistent with
    // `choice` + the current OS theme on every render.
    const osTheme = useSyncExternalStore(subscribeOsTheme, getOsTheme, getOsThemeSSR);
    const resolved: ResolvedTheme = choice === 'system' ? osTheme : choice;

    // Apply to <html> and meta any time the resolved theme changes.
    useEffect(() => {
        applyDomTheme(resolved);
    }, [resolved]);

    const setChoice = useCallback((next: ThemeChoice) => {
        try {
            window.localStorage.setItem(STORAGE_KEY, next);
        } catch (err) {
            captureError(err, { scope: 'ThemeProvider', op: 'setChoice' });
        }
        // Cross-subdomain persistence — round-trips through the BE auth
        // pages and Next.js landing via the parent-domain cookie. Per
        // CANONICAL_PALETTE.md § "Theme switching".
        writeCookieChoice(next);
        setChoiceState(next);
    }, []);

    const cycle = useCallback(() => {
        setChoice(choice === 'system' ? 'light' : choice === 'light' ? 'dark' : 'system');
    }, [choice, setChoice]);

    const value = useMemo<ThemeContextValue>(
        () => ({ choice, resolved, setChoice, cycle }),
        [choice, resolved, setChoice, cycle],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
    const ctx = useContext(ThemeContext);
    if (!ctx) {
        throw new Error('useTheme must be used within <ThemeProvider>');
    }
    return ctx;
}
